/**
 * What a work card may say about a work, wherever it is drawn: the collection
 * grid, the dashboard, the work record's own header.
 *
 * In `lib` because three screens draw the same card and a badge that
 * disagreed between them would tell the reader two things about one object.
 * The rules here are restraint rules — a headcount of names and never a
 * verdict, a withheld location said as withheld, an undisclosed owner as a
 * state rather than a null — and each is argued on the function that keeps
 * it. The words come from `constants.ts`.
 */

import { isOwner } from "@ondo/shared";
import type { CollectionSummary, MeResponse, WorkSummary } from "@ondo/shared";

import { LOCATION_LABELS, WORK_COPY } from "./constants.ts";

/**
 * What the registry can say about a work.
 *
 * Which is, now, only who has put their name to its chain of title.
 * 20260804090700 removed the review model, and with it `works.verification` —
 * nothing authenticates anything, so a badge claiming otherwise would be a
 * claim the registry has stopped being able to make.
 */
export type WorkBadge =
  | { kind: "verified"; label: string }
  | { kind: "contested"; label: string }
  | { kind: "unverified"; label: string };

/**
 * Who owns a collection.
 *
 * A union rather than a nullable name, because "not disclosed" is a real and
 * permanent state here rather than missing data. `collection_grants_read` shows
 * the owner grant only to owners, and `principals_read` is `acts_as(id)` — so
 * for a collection shared *with* the caller the owning principal is
 * undisclosable by policy, not merely unknown. A `null` would invite an
 * "Unknown" bucket that quietly means "somebody else's".
 */
export type OwnerRef =
  | { kind: "acted-for"; label: string }
  /**
   * A NAMED ENTITY the caller acts for — OND-52 part (b). The `entity` variant
   * this function's own comment promised when "an owner principal does arrive",
   * which it did in OND-184's `CollectionSummary.ownerOndoId`.
   *
   * `ondoId` beside the label because a screen aggregating several entities has
   * to group by something stable: two of a person's entities may share a name,
   * and the id is the thing the registry agrees with.
   */
  | { kind: "entity"; label: string; ondoId: string }
  | { kind: "undisclosed"; label: string };

/**
 * The entities the caller may name, keyed by ONDO id.
 *
 * NOTHING IS LOOKED UP AND NOTHING IS FETCHED. Every name in here came from the
 * caller's own account read — their principal, and each organisation they hold a
 * seat at — so resolving an `ownerOndoId` through it cannot name a party they
 * could not already name. That is the whole reason `toOwnerRef` takes a map
 * rather than an id to resolve: a function that went looking would be a
 * directory, and CLAUDE.md's "An ONDO id is attached, never discovered" is
 * about exactly the lookup this avoids.
 *
 * An id absent from the map stays `undisclosed`, which is the honest answer and
 * the common one: `ondo.my_collections()` is SECURITY INVOKER, so a collection
 * a stranger shared arrives with a NULL owner rather than an id to miss.
 */
export type EntityNames = ReadonlyMap<string, string>;

/**
 * How many people have put their name to this work's chain of title.
 *
 * A HEADCOUNT OF SIGNATURES, not of entries: two people signing one link is
 * two, and one person signing three links is one. The product sentence is "what
 * other people will put their name to", and that is a count of names.
 *
 * `contested` when anybody has disputed, whatever the verifications say. There
 * is no threshold at which this resolves into a verdict, and none here: a card
 * showing "3 verified" while somebody is disputing would be the client deciding
 * a question the registry deliberately does not answer.
 *
 * Note what this is NOT: the evidence grade on a provenance entry. That is a
 * submitter describing their own paperwork, and it is spelled with different
 * words for exactly this reason.
 */
export function workBadge(work: WorkSummary): WorkBadge {
  if (work.disputedCount > 0) {
    return { kind: "contested", label: describeCount(work.verifiedCount, work.disputedCount) };
  }
  if (work.verifiedCount > 0) {
    return { kind: "verified", label: describeCount(work.verifiedCount, 0) };
  }
  return { kind: "unverified", label: WORK_COPY.noVerifications };
}

function describeCount(verified: number, disputed: number): string {
  const parts = [
    verified > 0 ? `${String(verified)} verified` : "",
    disputed > 0 ? `${String(disputed)} disputed` : "",
  ].filter((part) => part !== "");

  return parts.join(" · ");
}

/**
 * Where the work is, in words.
 *
 * A null `state` is a refusal, not a gap: a work reached through a work grant
 * discloses no location, because a piece shown for a valuation must not also
 * disclose the warehouse it came from. Saying so beats an empty cell that reads
 * as a bug.
 */
export function describeLocation(work: WorkSummary): string {
  if (work.state === null) return WORK_COPY.locationWithheld;
  if (work.state.onLoan) {
    return work.state.loanVenue ? `On loan · ${work.state.loanVenue}` : "On loan";
  }
  if (work.state.locationKind === null) return "";

  const label = LOCATION_LABELS[work.state.locationKind];
  return work.state.placeText ? `${label} · ${work.state.placeText}` : label;
}

/**
 * What the caller's own principal is called in a holder line.
 *
 * "Yours" and not their name: a person reading their own collection list is the
 * one entity on it that needs no introduction, and their name beside three
 * company names would read as a fourth company. The entity map carries this
 * string under the caller's own ONDO id, which is what makes the `acted-for`
 * branch below a lookup rather than a second rule.
 */
export const SELF_ENTITY_LABEL = "Yours";

/**
 * Every entity the caller acts for, keyed by ONDO id, for `toOwnerRef`.
 *
 * The caller's own principal is in it under `SELF_ENTITY_LABEL` rather than
 * under their name — see that constant for why "Yours" beats "Michael Rennie"
 * in a list whose other rows are companies.
 *
 * NULL IS AN EMPTY MAP, not a throw: an account that has not loaded yet leaves
 * every holder line at the two states it had before, which is the one answer
 * that cannot be wrong while the reader waits.
 *
 * ACTIVE SEATS ONLY. An ended or suspended seat is not an entity you act for,
 * and `ondo.principal_ids_at_least()` would not return the firm either — so a
 * row it named would be a name beside holdings the caller cannot reach.
 */
export function entityNamesOf(me: MeResponse | null): EntityNames {
  if (me === null) return new Map();

  const names = new Map<string, string>([[me.principal.ondoId, SELF_ENTITY_LABEL]]);

  for (const membership of me.memberships) {
    if (membership.status !== "active") continue;
    names.set(membership.organisation.ondoId, membership.organisation.displayName);
  }

  return names;
}

/**
 * Who holds this collection.
 *
 * THREE STATES SINCE OND-52, and the middle one is the ticket: "a work's record
 * cannot tell you which entity holds it". A collection owned by a company, a
 * trust or a foundation the caller administers used to read "Yours" when their
 * effective level was owner and "Shared with you" when it was not — neither of
 * which is what a person wants to know when their pictures are held through
 * three legal owners.
 *
 * `entities` is OPTIONAL, and the default is the behaviour every existing caller
 * had. A screen that has not been given the map is not shown a wrong name; it is
 * shown the two states it was always shown.
 *
 * The order of the tests is load-bearing. `ownerOndoId` is asked FIRST, because
 * a firm-owned collection the caller is only a viewer of is still that firm's
 * and saying so is the point; "Yours" is then the caller's OWN principal, which
 * is in the map under their own id. An id the map does not hold falls through to
 * the level test and then to undisclosed, so an unexpected id can never be
 * printed as a name.
 */
export function toOwnerRef(
  collection: CollectionSummary,
  entities: EntityNames = new Map(),
): OwnerRef {
  const owner = collection.ownerOndoId;
  const name = owner === null ? undefined : entities.get(owner);

  if (owner !== null && name !== undefined) {
    return name === SELF_ENTITY_LABEL
      ? { kind: "acted-for", label: SELF_ENTITY_LABEL }
      : { kind: "entity", label: name, ondoId: owner };
  }

  return isOwner(collection.level)
    ? { kind: "acted-for", label: SELF_ENTITY_LABEL }
    : { kind: "undisclosed", label: WORK_COPY.sharedWithMe };
}
